import type { MouseEvent } from 'react'

import styled from 'styled-components'

import { ChevronUpIcon, ChevronDownIcon } from 'icons'
import copy from 'copy.json'
import Checkbox from './Checkbox'

export type OptionProps = { name: string; value: string; isChecked: boolean }

export type Props = {
  options: OptionProps[]
  allOptionsLabel?: string
  multipleSelectedLabel?: string
  onSelectionChange: (options: OptionProps[]) => void
}

export const getSelectedValues = (options: OptionProps[]) =>
  options.filter(option => option.isChecked).map(option => option.value)

export default function Multiselect({
  options,
  allOptionsLabel = copy.components.multiselect.all_statuses_label,
  multipleSelectedLabel = copy.components.multiselect.multiple_selected_label,
  onSelectionChange
}: Props) {
  const checkedOptions = options.filter(option => option.isChecked)
  const noOptionsChecked = checkedOptions.length === 0
  const oneOptionChecked = checkedOptions.length === 1
  const multipleOptionsChecked = checkedOptions.length > 1
  const allOptionsChecked = checkedOptions.length === options.length
  const firstCheckedOptionName = checkedOptions[0]?.name

  const toggleOption = (event: MouseEvent<HTMLButtonElement>, optionName: OptionProps['name']) => {
    event.preventDefault()

    onSelectionChange(
      options.map(option =>
        option.name === optionName ? { ...option, isChecked: !option.isChecked } : option
      )
    )
  }

  const toggleAllOptions = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()

    onSelectionChange(options.map(option => ({ ...option, isChecked: !allOptionsChecked })))
  }

  return (
    <Wrapper tabIndex={1}>
      {noOptionsChecked && (
        <span style={{ color: 'var(--gray)' }}>{copy.components.multiselect.choose_label}</span>
      )}
      {oneOptionChecked && firstCheckedOptionName}
      {multipleOptionsChecked && `${multipleSelectedLabel} ${checkedOptions.length}`}

      <ChevronUpIcon className="chevronUp" />
      <ChevronDownIcon className="chevronDown" />

      <Options>
        <Option onMouseDown={toggleAllOptions}>
          <Checkbox checked={allOptionsChecked} readOnly />
          {allOptionsLabel}
        </Option>

        {options.map(option => (
          <Option key={option.value} onMouseDown={event => toggleOption(event, option.name)}>
            <Checkbox checked={option.isChecked} readOnly />
            {option.name}
          </Option>
        ))}
      </Options>
    </Wrapper>
  )
}

const Option = styled.button.attrs({ type: 'button' })`
  background-color: var(--white);
  border: 1px solid var(--light-gray);
  border-bottom: none;

  margin: 0;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  gap: 8px;

  font: var(--400-13);
  color: #333333;

  cursor: pointer;

  :last-of-type {
    border-bottom: 1px solid var(--light-gray);
  }
`

const Options = styled.div`
  position: absolute;
  top: 100%;
  right: -1px;
  left: -1px;

  display: flex;
  flex-direction: column;
`

const Wrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-sizing: border-box;
  position: relative;
  width: 100%;
  min-width: 196px;
  padding: 15px 16px;
  z-index: 10;

  border: 1px solid var(--light-gray);
  border-radius: 4px 4px 0 0;

  background-color: var(--white);
  color: #333333;

  :not(:focus) ${Options}, :not(:focus-within) ${Options} {
    display: none;
  }

  :focus-within .chevronDown {
    display: none;
  }

  :not(:focus) .chevronUp {
    display: none;
  }
`
